import axios from 'axios';

// safe access to environment variables
let API_BASE = '';
if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) {
    API_BASE = process.env.REACT_APP_API_BASE;
}
if (!API_BASE && typeof window !== 'undefined' && window.__API_BASE__) API_BASE = window.__API_BASE__;

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add interceptor to add token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// --- Admin Services ---

export const fetchAllUsers = async () => {
    const res = await api.get('/api/users');
    return res.data;
};

export const fetchPatientsList = async () => {
    const res = await api.get('/api/users/list/patients');
    return res.data;
};

export const fetchDoctorsList = async () => {
    const res = await api.get('/api/users/list/doctors');
    return res.data;
};

export const fetchDoctorCount = async () => {
    const res = await api.get('/api/users/count/doctors');
    return res.data.count;
};

export const fetchPatientCount = async () => {
    const res = await api.get('/api/users/count/patients');
    return res.data.count;
};

export const fetchAppointmentCount = async () => {
    const res = await api.get('/api/users/count/appointments');
    return res.data.count;
};

export const fetchDoctorsPerMonth = async () => {
    const res = await api.get('/api/users/per-month/doctors');
    return res.data;
};

export const fetchPatientsPerMonth = async () => {
    const res = await api.get('/api/users/per-month/patients');
    return res.data;
};

export const fetchAppointmentsPerMonth = async () => {
    const res = await api.get('/api/users/per-month/appointments');
    return res.data;
};

// Affectations
export const fetchActiveAffectations = async () => {
    const res = await api.get('/api/affectation/active');
    return res.data;
};

export const fetchPatientsWithoutAffectation = async () => {
    const res = await api.get('/api/affectation/patients-without-affectation');
    return res.data;
};

export const assignAffectation = async ({ patient_id, doctor_id, motif, notes }) => {
    const res = await api.post('/api/affectation/assign', { patient_id, doctor_id, motif, notes });
    return res.data;
};

export const unassignAffectation = async ({ affectation_id, motif, notes }) => {
    const res = await api.post('/api/affectation/unassign', { affectation_id, motif, notes });
    return res.data;
};

// User Management
export const registerUser = async (data) => {
    // Determine endpoint based on role if needed, or use generic
    // Note: register-doctor might not exist in backend routes/auth.js yet? 
    // Checking auth.js in step 90/94: only /register exists and handles roles.
    // So we should use /register for all unless legacy code used register-doctor.
    // Legacy UserList.jsx used: /api/auth/register-doctor
    // I should check if that route exists.
    // For now I'll point to /api/auth/register as the backend seems to support role in body.
    const res = await api.post('/api/auth/register', data);
    return res.data;
};

export const deleteUser = async (id) => {
    const res = await api.delete(`/api/users/${id}`);
    return res.data;
};

export const toggleUserActive = async (id, isActive) => {
    const res = await api.patch(`/api/users/${id}/active`, { is_active: isActive });
    return res.data;
};

// Messaging Services
export const fetchConversations = async () => {
    const res = await api.get('/api/messages/conversations');
    return res.data;
};

export const fetchMessages = async (partnerId) => {
    const res = await api.get(`/api/messages/${partnerId}`);
    return res.data;
};

export const sendMessage = async ({ receiver_id, content }) => {
    const res = await api.post('/api/messages', { receiver_id, content });
    return res.data;
};

export const markMessagesRead = async (partnerId) => {
    const res = await api.patch(`/api/messages/read/${partnerId}`);
    return res.data;
};

// Appointments
export const createAppointment = async (appointmentData) => {
    const res = await api.post('/api/appointments', appointmentData);
    return res.data;
};

export const fetchDoctorAppointments = async () => {
    const res = await api.get('/api/appointments/doctor/me');
    return res.data;
};

export const fetchPatientAppointments = async () => {
    const res = await api.get('/api/appointments/patient/me');
    return res.data;
};

export const cancelAppointment = async (id) => {
    const res = await api.patch(`/api/appointments/${id}/cancel`);
    return res.data;
};

export const rescheduleAppointment = async (id, date, time) => {
    const res = await api.patch(`/api/appointments/${id}/reschedule`, { date, time });
    return res.data;
};

// Consultations (Fiche Patient)
export const saveConsultation = async (consultationData) => {
    const res = await api.post('/api/consultations', consultationData);
    return res.data;
};

export const fetchConsultation = async (appointmentId) => {
    const res = await api.get(`/api/consultations/appointment/${appointmentId}`);
    return res.data;
};

// Doctors & Patients specific
export const fetchAllDoctors = async () => {
    const res = await api.get('/api/doctor/all');
    return res.data;
};

export const fetchMyPatients = async () => {
    const res = await api.get('/api/doctor/me/patients');
    return res.data;
};

// Patient Profile
export const fetchPatientProfile = async () => {
    const res = await api.get('/api/patient/me');
    return res.data;
};

export const updatePatientProfile = async (profileData) => {
    const res = await api.post('/api/patient/me', profileData);
    return res.data;
};

export const fetchMyDoctors = async () => {
    const res = await api.get('/api/patient/my-doctors');
    return res.data;
};

export const consultations = {
    request: () => api.post('/api/consultations/request'),
    getMyLatest: () => api.get('/api/consultations/my-latest'),
    getPending: () => api.get('/api/consultations/admin/pending'),
    schedule: (id, data) => api.patch(`/api/consultations/${id}/schedule`, data),
    confirm: (id) => api.patch(`/api/consultations/${id}/confirm`),
};

export const notifications = {
    getAll: () => api.get('/api/notifications'),
    getUnreadCount: () => api.get('/api/notifications/unread-count'),
    markRead: (id) => api.patch(`/api/notifications/${id}/read`),
    markAllRead: () => api.patch('/api/notifications/read-all'),
};

export default api;
